import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CompPagination, Container } from "components";
import { AiOutlinePlus } from "react-icons/ai";
import Breadcrumb from "components/molecules/Breadcrumb";
import { useDispatch, useSelector } from "react-redux";
import { deleteData, getData, putData } from "actions";
import { API_URL_templateSet } from "constants/api";
import { templateSetReducers } from "redux/templateSetSlice";
import useDebounce from "hooks/useDebounce";
import { BiSortDown, BiSortUp } from "react-icons/bi";
import { FaAdjust, FaEye } from "react-icons/fa";
import { SyncLoader } from "react-spinners";
import InputModalTemplateSet from "components/molecules/inputModalTemplateSet";

// field for use sort up/down
const TABLE_HEAD = [
  { title: "name", field: "" },
  { title: "status", field: "" },
  { title: "Actions", field: "" },
];

const Templates = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");
  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  const {
    getTemplateSetResult,
    getTemplateSetLoading,
    getTemplateSetError,
    addTemplateSetResult,
    deleteTemplateSetResult,
  } = useSelector((state) => state.templateSet);

  const get = useCallback(
    async (params) => {
      getData(
        API_URL_templateSet,
        params,
        { dispatch, redux: templateSetReducers },
        "GET_TEMPLATESET"
      );
    },
    [dispatch]
  );

  const onDelete = (item) => {
    deleteData(
      API_URL_templateSet + item.id,
      { dispatch, redux: templateSetReducers },
      "DELETE_TEMPLATESET"
    );
  };

  const onShow = (item) => {
    navigate(`/administrator/email/templates/view`, {
      state: {
        item,
      },
    });
  };

  const onActive = (item) => {
    const param = { active: item.active === 1 ? 0 : 1 };
    putData(
      `${API_URL_templateSet}${item.id}/`,
      param,
      { dispatch, redux: templateSetReducers },
      "ADD_TEMPLATESET",
      () => {
        fetchData();
      }
    );
  };

  const openModal = () => {
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  const handlePageClick = ({ selected }) => {
    const newOffset = selected * limit;
    const params = `?limit=${limit}&offset=${newOffset}&search=${search}&ordering=${
      sortOrder === "desc" ? "-" : ""
    }${sortColumn}`;
    setOffset(newOffset);
    setCurrentPage(selected);
    get(params);
  };

  const delayedSearch = useCallback(
    useDebounce((value) => {
      //RESET PAGINATE KE 1
      setOffset(0);
      setCurrentPage(0);
      const params = `?limit=${limit}&offset=${offset}&search=${value}&ordering=${
        sortOrder === "desc" ? "-" : ""
      }${sortColumn}`;
      get(params);
    }, 1000),
    [limit, offset, sortOrder, sortColumn]
  );

  const onSearch = (value) => {
    setSearch(value);
    delayedSearch(value);
  };

  const handleSort = (column) => {
    //RESET PAGINATE KE 1
    setOffset(0);
    setCurrentPage(0);
    if (column === sortColumn) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  // Sort icons
  const renderSortIcon = (field) => {
    if (field === sortColumn) {
      return sortOrder === "asc" ? (
        <BiSortUp className="ml-1 h-4" />
      ) : (
        <BiSortDown className="ml-1 h-4" />
      );
    }
    return <BiSortUp className="ml-1 h-4 text-gray-300" />;
  };

  const handleSelectLimit = (limit) => {
    //RESET PAGINATE KE 1
    setOffset(0);
    setCurrentPage(0);

    const params = `?limit=${limit}&offset=${offset}&search=${search}&ordering=${
      sortOrder === "desc" ? "-" : ""
    }${sortColumn}`;
    get(params);
    setLimit(limit);
  };

  const action = [
    {
      name: "Active",
      icon: <FaAdjust />,
      func: onActive,
      colorlight: "bg-green-500",
      colordark: "dark:bg-green-800",
      hoverlight: "hover:bg-green-600",
      hoverdark: "hover:dark:bg-green-900",
    },
    {
      name: "Show",
      icon: <FaEye />,
      func: onShow,
      colorlight: "bg-sky-500",
      colordark: "dark:bg-sky-800",
      hoverlight: "hover:bg-sky-600",
      hoverdark: "hover:dark:bg-sky-900",
    },
    // {
    //   name: "Delete",
    //   icon: <FaTrash />,
    //   func: onDelete,
    //   colorlight: "bg-red-500",
    //   colordark: "dark:bg-red-800",
    //   hoverlight: "hover:bg-red-600",
    //   hoverdark: "hover:dark:bg-red-900",
    // },
  ];

  const fetchData = useCallback(() => {
    const params = `?limit=${limit}&offset=${offset}&search=${search}&ordering=${
      sortOrder === "desc" ? "-" : ""
    }${sortColumn}`;
    get(params);
  }, [get, limit, offset, sortColumn, sortOrder]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (addTemplateSetResult) {
      fetchData();
    }
  }, [addTemplateSetResult, fetchData]);

  useEffect(() => {
    if (deleteTemplateSetResult) {
      fetchData();
    }
  }, [deleteTemplateSetResult, fetchData]);

  return (
    <Container>
      <Breadcrumb
        title={"Template set"}
        excludedPaths={["email", "administrator"]}
        group={"Superadmin"}
      />
      <div className="bg-neutral dark:bg-neutral-800 rounded overflow-hidden shadow">
        <div className="h-12 w-full grid items-center px-4 bg-neutral-100 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600 text-xs md:text-base">
          List of templates set
          <div className="col-end-7 flex justify-end">
            <button
              className="flex items-center gap-1 p-1 px-3 rounded shadow-sm border bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-700 dark:border-neutral-600 dark:hover:bg-neutral-800"
              onClick={openModal}
            >
              <AiOutlinePlus />
              Create
            </button>
          </div>
        </div>

        <div className="flex flex-col md:flex-row text-center md:justify-between align-middle px-4 my-4 bg-neutral dark:bg-neutral-800 ">
          <div className="p-1 inline-block align-middle">
            <select
              id="limit"
              value={limit}
              onChange={(e) => handleSelectLimit(e.target.value)}
              className="border rounded-sm p-1 border-gray-300 dark:text-black focus:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={500}>500</option>
            </select>
            <span className="mx-2 text-sm ">Records per page</span>
          </div>
          <div className="p-1 inline-block align-middle">
            <span className="text-sm mr-2">Search:</span>
            <input
              id="search"
              type="text"
              value={search}
              onChange={(e) => onSearch(e.target.value)}
              placeholder="by name"
              className="p-1 border border-gray-300 dark:text-black focus:border-blue-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>
        </div>

        {/* <CardContainer title="List of agents"> */}
        <div className="mx-4">
          <div className="overflow-x-auto min-h-[55vh]">
            <table className="border border-collapse items-center w-full">
              <thead className="text-left">
                <tr>
                  {TABLE_HEAD.map((item, itemIdx) => (
                    <th
                      key={itemIdx}
                      className="p-2 border text-sm whitespace-nowrap cursor-pointer"
                      onClick={() => {
                        item.field && handleSort(item.field);
                      }}
                    >
                      <span className="flex">
                        {item.title}
                        {item.field && renderSortIcon(item.field)}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* loading  */}
                {getTemplateSetLoading && (
                  <tr>
                    <td
                      className="text-center py-12"
                      colSpan={TABLE_HEAD.length + 1}
                    >
                      <div className="pt-10 pb-6 flex justify-center items-center">
                        <SyncLoader color="#4477CE" />
                      </div>
                    </td>
                  </tr>
                )}
                {/* Error */}
                {getTemplateSetError && (
                  <tr>
                    <td className="text-center" colSpan={TABLE_HEAD.length + 1}>
                      <div className="pt-20 pb-12 flex justify-center items-center text-xs text-red-500">
                        {getTemplateSetError}
                      </div>
                    </td>
                  </tr>
                )}
                {/* Result = 0 */}
                {getTemplateSetResult.results &&
                  getTemplateSetResult.results.length === 0 && (
                    <tr>
                      <td
                        className="text-center"
                        colSpan={TABLE_HEAD.length + 1}
                      >
                        <div className="pt-20 pb-12 flex justify-center items-center text-xs text-slate-600 dark:text-white">
                          No Data
                        </div>
                      </td>
                    </tr>
                  )}
                {getTemplateSetResult.results &&
                  getTemplateSetResult.results.map((item, index) => (
                    <tr
                      key={index}
                      className={`${
                        item.active ? "bg-gray-100 dark:bg-neutral-600" : ""
                      }`}
                    >
                      <td className="p-4 border border-blue-gray-50 w-4/12">
                        {item.name}
                      </td>
                      <td className="p-4 border border-blue-gray-50 w-3/12">
                        <p
                          className={`${
                            item.active ? "text-green-500" : "text-red-600"
                          } `}
                        >
                          {item.active ? "Active" : "Disable"}
                        </p>
                      </td>
                      <td className="p-4 border border-blue-gray-50">
                        <div className="flex">
                          {action.map((action, actionIdx) => (
                            <button
                              key={actionIdx}
                              className={`mx-0.5 text-white
                              ${
                                action.name === "Active" && item.active === 1
                                  ? "cursor-not-allowed"
                                  : ""
                              }
                               ${
                                 action.name === "Delete" && item.active === 1
                                   ? "cursor-not-allowed"
                                   : ""
                               } text-sm ${action.colorlight} ${
                                action.colordark
                              } ${action.hoverlight} ${
                                action.hoverdark
                              } rounded p-1 `}
                              onClick={() => action.func(item)}
                              disabled={
                                (action.name === "Delete" &&
                                  item.active === 1) |
                                (action.name === "Active" && item.active === 1)
                              }
                            >
                              <div className="flex items-center">
                                {action.icon} {action.name}
                              </div>
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <div className="pt-5 py-11">
            <CompPagination
              handlePageClick={(e) => handlePageClick(e)}
              pageCount={
                getTemplateSetResult.count > 0 ? getTemplateSetResult.count : 0
              }
              limit={limit}
              offset={offset}
              currentPage={currentPage}
            />
          </div>
        </div>
        {/* Modal */}
        <div>
          <InputModalTemplateSet
            isOpen={isOpen}
            onClose={closeModal}
            title={"Create"}
          />
        </div>
        {/* </CardContainer> */}
      </div>
    </Container>
  );
};

export default Templates;
